import { useLocation } from "wouter";

import { Accounts } from "../../../services";
import { ErrorState, AccountEmptyState } from "../../../components/States";
import { AccountPanel } from "../panels/AccountPanel";
import { ACTIVE_OPTIONS, DEFAULT_FILTERS } from "./constants";
import {
  useAccountsData,
  useAccountFilters,
  useAccountPanel,
  useYearNavigation,
} from "./hooks";
import {
  AccountsSkeleton,
  AccountsStats,
  AccountsFilters,
  AccountsList,
} from "./components";
import { CreateFab } from "../../../components/CreateFab";
import { useResponsive } from "../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../utils/constants";
import { Wrapper } from "./styles";

export const AccountsPage = () => {
  const [, setLocation] = useLocation();

  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const { years } = useYearNavigation();
  const { yearSelected, activeOnly, handleYearChange, handleActiveChange } =
    useAccountFilters({
      defaultYear: DEFAULT_FILTERS.yearSelected,
      defaultActive: DEFAULT_FILTERS.activeOnly,
    });

  const {
    accounts,
    totals,
    hasAnyAccounts,
    isLoading,
    error,
    refetchAccounts,
  } = useAccountsData({
    yearSelected,
    activeOnly,
  });

  const { panel, openPanel, closePanel, handleSubmit } = useAccountPanel();

  const navigateTo = (account: Accounts) => {
    setLocation("/accounts/" + account.id);
  };

  // Broadest view that still surfaces the user's accounts: most recent activity year, "active only" lifted.
  const showAllAccounts = () => {
    const latestYear = years.reduce(
      (latest, year) => (year.value > latest.value ? year : latest),
      years[0],
    );
    handleYearChange(latestYear);
    handleActiveChange(ACTIVE_OPTIONS[1]);
  };

  if (isLoading) {
    return <AccountsSkeleton />;
  }

  if (error) {
    return (
      <Wrapper>
        <ErrorState message={error} onRetry={refetchAccounts} />
      </Wrapper>
    );
  }

  const hasAccounts = accounts.length > 0;
  const isEmpty = !hasAccounts;
  // "No accounts yet" is based on account existence, not finance-wide activity — other assets/transactions can predate accounts.
  const showFirstRunEmpty = isEmpty && !hasAnyAccounts;
  const showFilteredEmpty = isEmpty && !showFirstRunEmpty;
  const selectedFilterLabel = yearSelected.label;
  // Empty states keep their own inline create button, so the FAB would be a competing duplicate there.
  const showCreateFab = isCompact && hasAccounts;

  const filteredEmptyDescription =
    "No accounts match the current filters. Adjust the filters or create a new account to see it here.";
  return (
    <Wrapper>
      {showFirstRunEmpty && (
        <AccountEmptyState
          onAddAccount={openPanel}
          buttonLabel="Create account"
        />
      )}

      {!showFirstRunEmpty && (
        <>
          {hasAccounts && (
            <AccountsStats
              currentAmount={totals?.currentAmount ?? 0}
              inAmount={totals?.inAmount ?? 0}
              outAmount={totals?.outAmount ?? 0}
              accountCount={accounts.length}
              selectedFilter={selectedFilterLabel}
              onCreateAccount={openPanel}
              showCreateButton={!isCompact}
            />
          )}

          {showFilteredEmpty && (
            <AccountEmptyState
              title="No accounts found"
              description={filteredEmptyDescription}
              onAddAccount={openPanel}
              buttonLabel="Create account"
              ariaLabel="No accounts found"
              secondaryActionLabel="Clear filters"
              onSecondaryAction={showAllAccounts}
            />
          )}

          <AccountsFilters
            isCompact={isCompact}
            years={years}
            yearSelected={yearSelected}
            onYearChange={handleYearChange}
            activeOnly={activeOnly}
            onActiveChange={handleActiveChange}
            resultCount={accounts.length}
          />

          {hasAccounts && (
            <AccountsList accounts={accounts} onAccountClick={navigateTo} />
          )}
        </>
      )}

      {showCreateFab && (
        <CreateFab onClick={openPanel} ariaLabel="Create account" />
      )}

      {panel.isOpen && (
        <AccountPanel
          handleOnSubmit={handleSubmit}
          isOpen={panel.isOpen}
          onClose={closePanel}
        />
      )}
    </Wrapper>
  );
};
